import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { isObject } from 'lodash';

import EasyPayOption from '../easy-pay-option/easy-pay-option';

import { DTCC_PROMOTION_MODAL_MESSAGE } from '../../shared/graphql/dtcc-queries';

import {
  useDTCCApplication,
  useMessage,
  useModal,
  useMyStore,
  useNavigateTo
} from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { interpolateCmsData } from '../../shared/utils/cms/cms';
import { getIsPromotional } from '../../shared/utils/dtcc-banner/dtcc-banner';
import { convertIntPlanDuration } from '../../shared/utils/payment-utils/payment-utils';

import {
  CarCareOnePromotionData,
  Maybe,
  Query
} from '../../shared/types/graphql-types';
import { MapPrice } from '../../shared/types/product';
import { DTCC_APPLICATION_STATUS } from '../../shared/constants/cart';
import {
  IMAGE_KEYS,
  MONTHLY_MESSAGING_KEYS,
  PROMOTIONAL_MESSAGING_KEYS
} from '../../shared/constants/dtcc-banner-constants';
import { ONE_CLICK_MODAL } from '../../shared/constants/modals';

interface DTCCBannerProps {
  applyNowButtonId: string;
  className?: string;
  combinedMapPrice?: MapPrice;
  formattedValue?: string;
  linkText?: string;
  showLinkAsCTA?: boolean;
  value?: number;
}

interface DTCCData {
  imageAltText: string;
  imageURL: string;
  linkText: string;
  linkURL: string;
  messages: { [key: string]: string };
  monthlyPayment: Maybe<number>;
  planDuration: Maybe<number>;
  promotionalText: string;
}

interface MonthlyPaymentThresholdsRefTypes {
  max: number;
  min: number;
}

function DTCCBanner(props: DTCCBannerProps) {
  const {
    applyNowButtonId,
    className = '',
    combinedMapPrice = null,
    formattedValue,
    linkText = 'learn how',
    showLinkAsCTA,
    value
  } = props;
  const { myStore } = useMyStore();
  const storeCode = myStore?.code;
  const { openModal } = useModal();
  const { isClientLoaded, dtccApplicationStatus } = useDTCCApplication();
  const navigateTo = useNavigateTo();
  const [data, setData] = useState<DTCCData>();
  const monthlyPaymentThresholdsRef = useRef<MonthlyPaymentThresholdsRefTypes>({
    max: 0,
    min: 0
  });
  const promoDataRef = useRef<Maybe<CarCareOnePromotionData> | undefined>();

  const valueAsNumber = typeof value === 'number' ? value : NaN;
  const { data: modalData } = useQuery<Query>(DTCC_PROMOTION_MODAL_MESSAGE, {
    fetchPolicy: 'network-only',
    skip:
      isNaN(valueAsNumber) ||
      (valueAsNumber >= monthlyPaymentThresholdsRef.current.min &&
        valueAsNumber <= monthlyPaymentThresholdsRef.current.max),
    variables: {
      locale: 'en',
      orderAmount: value,
      storeCode
    }
  });
  const planDuration = useMemo(
    () =>
      convertIntPlanDuration(
        modalData?.storeCheckout?.cc1PromotionMessage?.promoData
          ?.planDuration || promoDataRef.current?.planDuration
      ),
    [modalData]
  );

  const isPromotional = getIsPromotional(combinedMapPrice, planDuration);
  const textContentKeys = isPromotional
    ? PROMOTIONAL_MESSAGING_KEYS
    : MONTHLY_MESSAGING_KEYS;
  const imageContentKeys = window.isDT ? IMAGE_KEYS.dt : IMAGE_KEYS.at;
  const messages = useMessage(textContentKeys.concat(imageContentKeys));

  function handleOnClick() {
    trackAction(
      isPromotional
        ? 'dtcc_cta_apply_promotional_learnmore'
        : 'dtcc_cta_apply_monthly_learnhow'
    );
    isPromotional
      ? navigateTo(data?.linkURL)
      : openModal(ONE_CLICK_MODAL, {
          applyNowButtonId: applyNowButtonId,
          formattedValue: formattedValue,
          monthlyPayment: data?.monthlyPayment,
          planDuration: data?.planDuration,
          termsAndConditions:
            modalData?.storeCheckout?.cc1PromotionMessage?.promoData
              ?.oneClickModalTerms || promoDataRef.current?.oneClickModalTerms
        });
  }

  useEffect(() => {
    const monthlyPayment =
      planDuration && Math.ceil((value || 0) / planDuration);

    if (
      messages &&
      (isPromotional || (planDuration && monthlyPayment)) &&
      (monthlyPayment !== data?.monthlyPayment ||
        JSON.stringify(messages) !== JSON.stringify(data?.messages))
    ) {
      const messagesLinkText =
        messages.DTCC_PROMO_MODAL_TRIGGER_MESSAGE ??
        messages.DTCC_PROMO_MORE_DETAILS_LINK_MESSAGE;
      let interpolateFinancingDetails = messages.DTCC_PROMO_FINANCING_DETAILS;

      if (interpolateFinancingDetails) {
        interpolateFinancingDetails = interpolateCmsData(
          {
            financingPeriod: planDuration,
            monthlyPrice: monthlyPayment
          },
          messages.DTCC_PROMO_FINANCING_DETAILS
        );
      }

      setData({
        imageAltText:
          messages.DTCC_PROMO_IMAGE_DISCOUNTTIRE_ALTTEXT ??
          messages.DTCC_PROMO_IMAGE_AMERICASTIRE_ALTTEXT,
        imageURL:
          messages.DTCC_PROMO_IMAGE_DISCOUNTTIRE_URL ??
          messages.DTCC_PROMO_IMAGE_AMERICASTIRE_URL,
        linkText: messagesLinkText,
        linkURL: messages.DTCC_PROMO_MORE_DETAILS_LINK_URL,
        messages: messages,
        monthlyPayment: monthlyPayment,
        planDuration: planDuration,
        promotionalText:
          interpolateFinancingDetails ?? messages.DTCC_PROMO_FINANCING_AVAILABLE
      });
    }
  }, [data, isPromotional, messages, planDuration, value]);

  useEffect(() => {
    const promoData = modalData?.storeCheckout?.cc1PromotionMessage?.promoData;

    if (promoData) {
      monthlyPaymentThresholdsRef.current = {
        max: promoData?.maxOrderAmount || 0,
        min: promoData?.minOrderAmount || 0
      };
      promoDataRef.current = promoData;
    }
  }, [modalData]);

  if (
    !isClientLoaded ||
    !isObject(data) ||
    Object.keys(data).length === 0 ||
    dtccApplicationStatus === DTCC_APPLICATION_STATUS.INSUFFICIENT
  ) {
    return null;
  }

  return (
    <EasyPayOption
      className={`dtcc-apply-now ${className} ${
        isPromotional ? 'dtcc-apply-now--promotional' : ''
      }`}
      imageAlt={data?.imageAltText}
      imageUrl={data?.imageURL}
      linkText={combinedMapPrice ? data?.linkText : linkText}
      message={
        <span
          dangerouslySetInnerHTML={{
            __html: data?.promotionalText
          }}
        />
      }
      onClick={handleOnClick}
      showLinkAsCTA={showLinkAsCTA}
      title={`${window.siteName} credit card`}
    />
  );
}

export default DTCCBanner;
